/**
 * Buddy tokens, timing, copy, and face geometry.
 * Behavior toggles live in config.ts.
 */

export const BUDDY_TOKENS = {
  accent: '#FAF02A',
  face: '#000000',
  surfaceDark: '#19181A',
  hairlineDark: '#333333',
  surfaceLight: '#FFFFFF',
  surfaceLightAlt: '#F6F6F6',
  muted: '#DCDCDC',
  radiusBubble: '33px',
  radiusContainer: '25px',
  radiusPill: '100px',
  fontBody: '"Poppins", system-ui, sans-serif',
};


export const BUDDY_SLOTS = {
  heroRight:    { x: 22, y: 42, size: 102, placement: 'side', align: 'left' },
  featuresLeft: { x: 11, y: 78, size: 88,  placement: 'top',  align: 'left'  },
  productRight: { x: 88, y: 34, size: 96,  placement: 'top',  align: 'right' },
  ctaLeft:      { x: 13, y: 72, size: 88,  placement: 'top',  align: 'left'  },
  footerRight:  { x: 90, y: 84, size: 64,  placement: 'top',  align: 'right' },
};

export const EXPRESSIONS = ['neutral', 'wink', 'happy', 'surprised', 'sleepy', 'sad', 'love', 'curious'];

/* Default anchor map. Override via the `anchors` prop.
   `message` is bilingual; `placeholder: true` marks copy that is NOT approved. */
export const BUDDY_ANCHORS = [
  {
    id: 'hero',
    slot: 'heroRight',
    expression: 'neutral',
    theme: 'light',
    message: {
      en: 'Create any agent that works for you just by telling me..',
      de: 'Erstelle jeden Agenten, der für dich arbeitet — sag es mir einfach..',
      it: 'Crea qualsiasi agente che lavori per te, basta dirmelo..',
      fr: 'Crée n’importe quel agent qui travaille pour toi, dis-le-moi simplement..',
    },
  },
  {
    id: 'features',
    slot: 'featuresLeft',
    expression: 'neutral',
    theme: 'light',
    placeholder: true,
    message: {
      en: 'Plug-and-play, ready the moment you are.',
      de: 'Plug-and-play, sofort einsatzbereit.',
      it: 'Plug-and-play, pronto nel momento in cui lo sei tu.',
      fr: 'Plug-and-play, prêt dès que tu l’es.',
    },
  },
  {
    id: 'product',
    slot: 'productRight',
    expression: 'neutral',
    theme: 'dark',
    placeholder: true,
    message: {
      en: 'Routine work handles itself. You keep the interesting part.',
      de: 'Routinearbeit erledigt sich selbst. Der spannende Teil bleibt dir.',
      it: 'Il lavoro di routine si fa da solo. A te resta la parte interessante.',
      fr: 'Le travail de routine se fait tout seul. La partie intéressante te reste.',
    },
  },
  {
    id: 'cta',
    slot: 'ctaLeft',
    expression: 'wink',
    theme: 'light',
    placeholder: true,
    message: {
      en: 'Take a look — it takes a minute.',
      de: 'Schau rein — dauert eine Minute.',
      it: 'Dai un’occhiata — ci vuole un minuto.',
      fr: 'Jette un œil — ça prend une minute.',
    },
  },
  {
    id: 'footer',
    slot: 'footerRight',
    expression: 'neutral',
    theme: 'dark',
    message: null, // silent
  },
];

export const STORAGE_KEY = 'klax.buddy.dismissed';
export const MOBILE_QUERY = '(max-width: 767px)';
export const TYPE_SPEED_MS = 28;
export const TRAVEL_MS = 1100; // must match the .klax-buddy transform transition
export const APPROACH_MS = 900;
export const HOVER_LEAVE_MS = 250;
export const CLOSE_MS = 420;
export const SPEAK_DELAY_MS = 820; // he lands, then speaks
export const ROUTE_SPEAK_DELAY_MS = 200;
export const BUBBLE_W = 311;

export { ROUTE_PROMPTS as ROUTE_LINES, DEFAULT_PROMPT as DEFAULT_LINE } from './prompt';

export const LOOK_MAX_X = 7.5;
export const LOOK_MAX_Y = 5;
export const LOOK_RANGE = 420; // px from Buddy at which the look reaches full extent
export const LOOK_SPRING = 0.26; // acceleration toward the gaze target
export const LOOK_DAMP = 0.68; // < 1 leaves a small overshoot, which is what sells it
export const SACCADE_MIN = 1.6; // gaze must move this far before the eyes flick at all
export const LOOK_PARALLAX = 0.9; // the far eye travels slightly less than the near one
export const IDLE_MS = 1300; // cursor quiet for this long and he starts glancing around
export const WANDER_MIN_MS = 900;
export const WANDER_MAX_MS = 2600;
export const WANDER_REACH = 0.55; // idle glances stay inside this fraction of the range
export const BLINK_JUMP = 4.5; // a saccade bigger than this may come with a blink
export const BLINK_JUMP_CHANCE = 0.3;

/* ---------------------------------------------------------------------------
 * Face geometry — viewBox 0 0 200 200.
 *
 * Ratios measured off the three faces drawn in KLAX!.fig (image.png):
 *   core diameter        73px
 *   eye box              12 x 16px  -> 0.164D wide, 0.219D tall
 *   eye centre spacing   24px       -> 0.329D, i.e. one eye-width of gap
 *   chevron box          13 x 14px  -> 0.178D wide, 0.192D tall
 *   eyes sit on the vertical centre, not above it
 * ------------------------------------------------------------------------ */

export const CORE_R = 66; // solid, fully opaque body
export const HALO_R = 99; // decorative bloom only — the body itself is never see-through
export const D = CORE_R * 2;

export const EYE_Y = 100;
export const EYE_DX = 0.1645 * D; // 21.7 — half the centre-to-centre spacing
export const EYE_RX = 0.082 * D; //  10.8
export const EYE_RY = 0.1095 * D; // 14.5
export const CHEV_SW = 0.062 * D; //   8.2 stroke weight
export const CHEV_HW = 0.089 * D - CHEV_SW / 2; // path half-width, stroke included in the box
export const CHEV_HH = 0.096 * D - CHEV_SW / 2; // path half-height

export const EYE_L_X = 100 - EYE_DX;
export const EYE_R_X = 100 + EYE_DX;

export const FACES = {
  neutral: { left: 'dot', right: 'dot' },
  wink: { left: 'chevronRight', right: 'dot' },
  happy: { left: 'chevronRight', right: 'chevronLeft' },
  surprised: { left: 'wide', right: 'wide' },
  sleepy: { left: 'flat', right: 'flat' },
  sad: { left: 'chevronDown', right: 'chevronDown' },
  love: { left: 'heart', right: 'heart' },
  curious: { left: 'wide', right: 'flat' },
};

export const EMOTES = ['wink', 'happy', 'surprised', 'sleepy', 'sad', 'love', 'curious'];
